import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { api } from '../App';
import { toast } from 'sonner';
import { Check, X, Zap, Users, Building2, Sparkles } from 'lucide-react';

export default function Pricing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingTier, setProcessingTier] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/payments/pricing/plans');
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      toast.error('Planlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tier) => {
    const token = localStorage.getItem('access_token');
    
    if (!token) {
      toast.error('Abone olmak için giriş yapmalısınız');
      navigate('/login');
      return;
    }

    if (tier === 'free') {
      toast.info('Zaten ücretsiz plana sahipsiniz');
      return;
    }

    if (tier === 'enterprise') {
      toast.info('Kurum paketi için lütfen bizimle iletişime geçin');
      return;
    }

    setProcessingTier(tier);

    try {
      // Get current origin
      const origin = window.location.origin;
      
      const response = await api.post('/payments/checkout/session', {
        tier: tier,
        success_url: `${origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${origin}/pricing`
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Ödeme başlatılamadı');
    } finally {
      setProcessingTier(null);
    }
  };

  const getPlanIcon = (tier) => {
    switch (tier) {
      case 'free':
        return <Sparkles className="w-8 h-8" />;
      case 'individual':
        return <Zap className="w-8 h-8" />;
      case 'professional':
        return <Users className="w-8 h-8" />;
      case 'enterprise':
        return <Building2 className="w-8 h-8" />;
      default:
        return <Sparkles className="w-8 h-8" />;
    }
  };

  const getPlanColor = (tier) => {
    switch (tier) {
      case 'free':
        return 'from-slate-500 to-slate-600';
      case 'individual':
        return 'from-blue-500 to-blue-600';
      case 'professional':
        return 'from-emerald-500 to-emerald-600';
      case 'enterprise':
        return 'from-purple-500 to-purple-600';
      default:
        return 'from-slate-500 to-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-btn">
            ← Geri Dön
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-16 animate-fade-in">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-700 to-emerald-700 bg-clip-text text-transparent">
            Size Uygun Planı Seçin
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Mizaç360 ile öğrencilerin, ailelerin ve eğitimcilerin potansiyelini ortaya çıkarın
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {plans.map((plan) => {
            const isPopular = plan.tier === 'individual';
            const isEnterprise = plan.tier === 'enterprise';
            
            return (
              <Card
                key={plan.tier}
                className={`relative shadow-xl border-2 ${
                  isPopular ? 'border-blue-500 scale-105' : 'border-transparent'
                }`}
                data-testid={`plan-${plan.tier}`}
              >
                {isPopular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600">
                    En Popüler
                  </Badge>
                )}

                <CardHeader>
                  <div className={`w-16 h-16 bg-gradient-to-br ${getPlanColor(plan.tier)} rounded-2xl flex items-center justify-center text-white mb-4 mx-auto`}>
                    {getPlanIcon(plan.tier)}
                  </div>
                  <CardTitle className="text-2xl text-center">{plan.name}</CardTitle>
                  <CardDescription className="text-center">{plan.description}</CardDescription>
                  
                  <div className="text-center py-4">
                    {plan.price_monthly === 0 ? (
                      <div className="text-4xl font-bold text-slate-800">Ücretsiz</div>
                    ) : isEnterprise ? (
                      <div className="text-3xl font-bold text-slate-800">Özel Fiyat</div>
                    ) : (
                      <>
                        <div className="text-4xl font-bold text-slate-800">
                          ₺{plan.price_monthly}
                          <span className="text-lg font-normal text-slate-600">/ay</span>
                        </div>
                        {plan.features.trial_days > 0 && (
                          <p className="text-sm text-emerald-600 mt-2">
                            {plan.features.trial_days} gün ücretsiz deneme
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      {plan.features.max_reports_per_month === -1 ? (
                        <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      ) : plan.features.max_reports_per_month > 0 ? (
                        <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      )}
                      <span className="text-sm text-slate-700">
                        {plan.features.max_reports_per_month === -1
                          ? 'Sınırsız rapor'
                          : `${plan.features.max_reports_per_month} rapor/ay`}
                      </span>
                    </li>
                    
                    <li className="flex items-start gap-2">
                      {plan.features.pdf_export ? (
                        <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      )}
                      <span className="text-sm text-slate-700">PDF Rapor İndirme</span>
                    </li>
                    
                    <li className="flex items-start gap-2">
                      {plan.features.advanced_reports ? (
                        <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      )}
                      <span className="text-sm text-slate-700">Gelişmiş Raporlar</span>
                    </li>
                    
                    {plan.features.max_children !== undefined && (
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">
                          {plan.features.max_children === -1
                            ? 'Sınırsız çocuk profili'
                            : `${plan.features.max_children} çocuk profili`}
                        </span>
                      </li>
                    )}
                    
                    {plan.features.max_students > 0 && (
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">
                          {plan.features.max_students === -1
                            ? 'Sınırsız öğrenci'
                            : `${plan.features.max_students} öğrenci`}
                        </span>
                      </li>
                    )}
                    
                    <li className="flex items-start gap-2">
                      {plan.features.priority_support ? (
                        <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      )}
                      <span className="text-sm text-slate-700">Öncelikli Destek</span>
                    </li>
                  </ul>

                  <Button
                    className={`w-full ${
                      isPopular
                        ? 'bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700'
                        : 'bg-slate-800 hover:bg-slate-900'
                    }`}
                    onClick={() => handleSubscribe(plan.tier)}
                    disabled={processingTier === plan.tier || plan.tier === 'free'}
                    data-testid={`subscribe-${plan.tier}-btn`}
                  >
                    {processingTier === plan.tier ? (
                      'İşleniyor...'
                    ) : plan.tier === 'free' ? (
                      'Mevcut Plan'
                    ) : isEnterprise ? (
                      'İletişime Geç'
                    ) : (
                      'Başlat'
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Enterprise Details */}
        <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold text-purple-900 mb-4">Kurum Paketleri</h3>
            <p className="text-purple-800 mb-6">
              Okullar ve eğitim kurumları için öğrenci sayısına göre özel fiyatlandırma sunuyoruz.
            </p>
            
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg">
                <p className="font-semibold text-purple-900">0-100 Öğrenci</p>
                <p className="text-2xl font-bold text-purple-700">₺2,000/ay</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="font-semibold text-purple-900">101-250 Öğrenci</p>
                <p className="text-2xl font-bold text-purple-700">₺4,000/ay</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="font-semibold text-purple-900">251-500 Öğrenci</p>
                <p className="text-2xl font-bold text-purple-700">₺7,000/ay</p>
              </div>
            </div>
            
            <p className="text-purple-800 text-sm">
              500+ öğrenci için özel teklif alın. İletişim: info@mizac360.com
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
